/** client/src/Dashboard.js **/

import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import Split from 'react-split';
import { Menu } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { UserCircleIcon } from '@heroicons/react/24/solid';
import { TrashIcon } from '@heroicons/react/24/outline';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Pencil, MessageSquarePlus, MessagesSquare, Check, X as XIcon } from 'lucide-react';

import { toast } from 'react-hot-toast';

import {TableIcon, CompactIcon, ListIcon, ElverdonLogo } from './components/Icons';

import ProjectTabs, { NewProjectDialogContent } from './components/Projects';
import useFeedPreview from './hooks/useFeedPreview';
import FieldNote from './components/FieldNote';
import DossierChat from './components/DossierChat';
import DocumentCommentPanel from './components/DocumentCommentPanel';

import {
  FaBook,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileCsv,
  FaFileAlt,
  FaFile,
  FaFileImage,
  FaFileAudio,
  FaFileVideo,
  FaFilePowerpoint,
  FaFileUpload,
  FaFont,
  FaFileCode,
  FaRssSquare,

  FaRegFile,
  FaRss,

  FaRegClipboard,

} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL;

const platform = navigator.platform.toLowerCase();
const isMac = platform.includes('mac');
const isMacOrLinux = platform.includes('mac') || platform.includes('linux');
// const isWindows = platform.includes('win');

const Dashboard = ({ user, onLogout }) => {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(-1);
  const [showInsight, setShowInsight] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [search, setSearch] = useState('');
  const [useNaturalLanguage, setUseNaturalLanguage] = useState(true);
  const [sortBy, setSortBy] = useState('retrieval_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [activeDialog, setActiveDialog] = useState(null); // 'upload' | 'newsletter' | 'project'
  const [newProject, setNewProject] = useState({ title: '', objective: '', rss: '' });
  const [isDragging, setIsDragging] = useState(false);
  const [thumbnailErrors, setThumbnailErrors] = useState({});
  const [showViewer, setShowViewer] = useState(false);
  const [viewerTab, setViewerTab] = useState('preview'); // preview | transcript
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, doc: null });
  const [showProjectSubmenu, setShowProjectSubmenu] = useState(false);
  const [previewMode, setPreviewMode] = useState('thumbnail'); // icon | thumbnail
  const [listMode, setListMode] = useState('compact'); //  compact | list | table
  const [groupByFeed, setGroupByFeed] = useState(false);
  const [expandedFeeds, setExpandedFeeds] = useState({});
  const [showChat, setShowChat] = useState(false);
  const [showCommentPanel, setShowCommentPanel] = useState(false);
  const [docNoteCount, setDocNoteCount] = useState(0);
  const [fieldNoteCounts, setFieldNoteCounts] = useState({});
  const [editingField, setEditingField] = useState(null); // fieldKey | null
  const [editValue, setEditValue] = useState('');
  const [newDoc, setNewDoc] = useState({
    title: '',
    summary: '',
    document_date: '',
    retrieval_date: '',
    url: '',
    files: []
  });

  const addCategoryToDoc = async (doc, category) => {
    if (!doc || !category) return;

    try {
      const res = await axios.post(`${API_URL}/api/documents/${doc.id}/add-category`, { category }, {
        withCredentials: true
      });

      setDocuments(prev =>
        prev.map(d =>
          d.id === doc.id
            ? { ...d, categories: res.data.categories }
            : d
        )
      );
    } catch (err) {
      console.error('Add category failed:', err);
      alert('Failed to add category');
    }
  };

  const handleFieldSave = async (fieldKey) => {
    if (!selectedDoc) return;
    try {
      const res = await axios.patch(`${API_URL}/api/documents/${selectedDoc.id}`, {
        [fieldKey]: editValue
      }, { withCredentials: true });
      setSelectedDoc(prev => ({ ...prev, [fieldKey]: res.data[fieldKey] }));
      setDocuments(prev => prev.map(d => d.id === selectedDoc.id ? { ...d, [fieldKey]: res.data[fieldKey] } : d));
      setEditingField(null);
    } catch (err) {
      console.error('Failed to save field', err);
      toast.error('Failed to save');
    }
  };

  const fetchDocuments = async () => {

    if (selectedCollection === 'all') {
      try {
        const res = await axios.get(`${API_URL}/api/documents`, {
          params: { search, sortBy, sortDir: sortDirection, natural: useNaturalLanguage },
          withCredentials: true
        });
        setDocuments(res.data);
      } catch (err) {
        console.error('Failed to load all documents:', err);
      }
    } else if (selectedCollection > -1) {
      try {
        const res = await axios.get(`${API_URL}/api/documents`, {
          params: { search, sortBy, sortDir: sortDirection, natural: useNaturalLanguage, projectId: selectedCollection },
          withCredentials: true
        });
        setDocuments(res.data);
      } catch (err) {
        console.error('Failed to load documents for project:', err);
      }
    }

  };

  const fetchFeeds = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/feeds`, { withCredentials: true });
      setFeeds(res.data);
    } catch (err) {
      console.error('Failed to load feeds:', err);
    }
  };

useEffect(() => {
  fetchProjects();
  fetchFeeds();

  const handleClickOutside = () => {
    setContextMenu(prev => ({ ...prev, visible: false }));
  };
  window.addEventListener('click', handleClickOutside);
  return () => window.removeEventListener('click', handleClickOutside);
}, []);

const fetchProjects = async () => {
  try {
    const res = await axios.get(`${API_URL}/api/projects`, {
      withCredentials: true
    });
    setCollections(res.data); 
    if (res.data.length === 0 ||
      (res.data.length > 0 && (selectedCollection === -1 || selectedCollection > res.data.length))) {
      setSelectedCollection('all'); // Select all by default
    }
  } catch (err) {
    console.error('Failed to load projects:', err);
  }
};

useEffect(() => {
    fetchDocuments();
}, [search, sortBy, sortDirection, useNaturalLanguage, selectedCollection]);

useEffect(() => {
  if (activeDialog === 'upload' && urlInputRef.current) {
    urlInputRef.current.focus();
  }
}, [activeDialog]);

useEffect(() => {
  if (selectedDoc?.id) {
    setThumbnailErrors(prev => {
      const updated = { ...prev };
      delete updated[selectedDoc.id]; // clear only current doc's error
      return updated;
    });
  }
  if (selectedItemRef.current) {
    selectedItemRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  }
  if (audioRef.current) {
    audioRef.current.load();
  }
  if (videoRef.current) {
    videoRef.current.load();
  }
  if (selectedDoc && window.innerWidth < 768) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }

  // Reset edit state when switching documents
  setEditingField(null);
  setEditValue('');

  // Fetch all note counts for this document
  if (selectedDoc?.id) {
    axios.get(`${API_URL}/api/notes`, {
      params: { entity_type: 'document', entity_id: selectedDoc.id },
      withCredentials: true
    }).then(res => {
      const counts = {};
      res.data.forEach(n => {
        counts[n.field_key] = (counts[n.field_key] || 0) + 1;
      });
      setFieldNoteCounts(counts);
      setDocNoteCount(res.data.length);
    }).catch(() => {});
  } else {
    setFieldNoteCounts({});
    setDocNoteCount(0);
  }

  /* if (selectedDoc?.file?.endsWith('.epub')) {
    const script = document.createElement('script');
    script.src = "https://cdn.jsdelivr.net/npm/epubjs/dist/epub.min.js";
    script.onload = () => {
      / * global ePub * /

      const book = ePub(`${API_URL}/api/documents/${selectedDoc.id}/download`);
      const rendition = book.renderTo("epub-reader", {
        width: "100%",
        height: "100%",
      });
      rendition.display();
    };
    document.body.appendChild(script);
  } */

}, [selectedDoc]);

useEffect(() => {
  const handleKeyDown = (e) => {
    if (!selectedDoc || !['ArrowUp', 'ArrowDown'].includes(e.key)) return;

    const index = documents.findIndex(doc => doc.id === selectedDoc.id);
    if (index === -1) return;

    if (e.key === 'ArrowUp' && index > 0) {
      setSelectedDoc(documents[index - 1]);
      e.preventDefault();
    } else if (e.key === 'ArrowDown' && index < documents.length - 1) {
      setSelectedDoc(documents[index + 1]);
      e.preventDefault();
    }
  };

  window.addEventListener('keydown', handleKeyDown);
  return () => {
    window.removeEventListener('keydown', handleKeyDown);
  };
}, [selectedDoc, documents]);

useEffect(() => {
  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files) {
      setNewDoc({ ...newDoc, files });
      setActiveDialog('upload');
      // Let React render the dialog with file input, then trigger form submission
        setTimeout(() => {
        if (formRef.current) {
            formRef.current.requestSubmit();
        }
        }, 0);
    }
    setIsDragging(false);
  };
  const handleDragLeave = () => setIsDragging(false);

  window.addEventListener('dragenter', handleDragEnter);
  window.addEventListener('dragover', handleDragOver);
  window.addEventListener('drop', handleDrop);
  window.addEventListener('dragleave', handleDragLeave);

  return () => {
    window.removeEventListener('dragenter', handleDragEnter);
    window.removeEventListener('dragover', handleDragOver);
    window.removeEventListener('drop', handleDrop);
    window.removeEventListener('dragleave', handleDragLeave);
  };
}, [newDoc]);

  // pass handler to NewProjectDialogContent
  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const rssList = newProject.rss.split(/\s+/).map(u => u.trim()).filter(Boolean);
      const res = await axios.post(`${API_URL}/api/projects`, {
        title: newProject.title,
        objective: newProject.objective,
        rss: rssList
      }, { withCredentials: true });

      setCollections(prev => [...prev, res.data]);
      setSelectedCollection(res.data.id);
      setNewProject({ title: '', objective: '', rss: '' });
      setActiveDialog(null);
    } catch (err) {
      toast.error('Failed to create project');
    }
  };

  const handleDelete = async () => {
    if (!selectedDoc) return;
    const confirm = window.confirm('Are you sure you want to delete this document?');
    if (!confirm) return;

    try {
        await axios.delete(`${API_URL}/api/documents/${selectedDoc.id}`, {
        withCredentials: true
        });
        setSelectedDoc(null);
        fetchDocuments(); // Refresh list
    } catch (err) {
        console.error('Delete failed:', err);
        alert('Failed to delete document.');
    }
  };

  const extractNameFromUrl = (url) => {
    try {
      const parsed = new URL(url);
      const pathname = parsed.pathname.replace(/\/$/, ''); // remove trailing slash
      const lastSegment = pathname.split('/').filter(Boolean).pop() || parsed.host;
      return decodeURIComponent(lastSegment);
    } catch {
      return undefined;
    }
  };
  const handleUpload = async (e) => {
    e.preventDefault();

    const urls = newDoc.url
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line);

    const uploads = [];

    if (newDoc.files && newDoc.files.length > 0) {
      uploads.push(...newDoc.files.map(file => ({ file })));
    }

    if (urls.length > 0) {
      uploads.push(...urls.map(url => ({ url })));
    }

    if (uploads.length === 0) {
      alert('Please provide at least one file or URL.');
      return;
    }

    setActiveDialog(null);

    // Process each URL separately
    // const submissions = urls.length > 0 ? urls : [null];

    for (const item of uploads) {
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
      const title = item.url ? shortenUrl(item.url) : item.file.name;

      const pendingDoc = {
        id: tempId,
        title,
        summary: '',
        document_date: '',
        isProcessing: true
      };

      setDocuments(prev => [pendingDoc, ...prev]);

      const formData = new FormData();
      if (item.file) formData.append('file', item.file);
      if (item.url) formData.append('url', item.url);
      if (selectedCollection > 0) {
        formData.append('projectId', selectedCollection);
      }

      ['title', 'summary', 'document_date', 'retrieval_date'].forEach(key => {
        if (newDoc[key]) formData.append(key, newDoc[key]);
      });

      try {
        await axios.post(`${API_URL}/api/documents`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        });
      } catch (err) {
        alert(`Upload failed for ${item.url || item.file?.name}`);
        setDocuments(prev => prev.filter(doc => doc.id !== tempId));
      }
    }

    fetchDocuments(); // refresh all once done

    setNewDoc({
      title: '',
      summary: '',
      document_date: '',
      retrieval_date: '',
      url: '',
      files: []
    });
  };

  const formRef = useRef();
  const fileInputRef = useRef(null);
  const selectedItemRef = useRef();

  const urlInputRef = useRef(null);
  const audioRef = useRef(null);
  const videoRef = useRef(null);

  const currentProject = collections.find((col) => col.id === selectedCollection);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const sortOptions = [
    { value: 'document_date', label: 'Document Date' },
    { value: 'retrieval_date', label: 'Retrieval Date' }
  ];

  const modeIconMap = {
    compact: <CompactIcon />,
    list:   <ListIcon />,
    table:  <TableIcon />
  }

  // const fileType = selectedDoc?.file?.split('.').pop().toLowerCase();
  // const isFeed = fileType === 'xml' || fileType === 'rss' || fileType === 'atom';

  const selectedFeedTitles = (typeof(selectedDoc?.feed_titles) === "string") ? [selectedDoc?.feed_titles] : selectedDoc?.feed_titles;
  const feed = useFeedPreview(selectedDoc?.id);

  const groupedFeeds = groupByFeed
    ? documents.reduce((acc, doc) => {
        const feedIds = Array.isArray(doc.feed_ids)
          ? doc.feed_ids
          : doc.feed_ids
          ? [doc.feed_ids]
          : [];

        const feedTitles = Array.isArray(doc.feed_titles)
          ? doc.feed_titles
          : doc.feed_titles
          ? [doc.feed_titles]
          : [];

        if (feedIds.length > 0) {
          feedIds.forEach((feedId, idx) => {
            const feedMeta = feeds.find(f => parseInt(f.id) === parseInt(feedId));
            const title = feedMeta?.title || feedTitles[idx] || (feedMeta?.url ? extractNameFromUrl(feedMeta.url) : 'Untitled Feed');
            if (!acc[feedId]) {
              acc[feedId] = {
                feed_id: feedId,
                feed_title: title,
                feed_url: feedMeta?.url || null,
                feed_lastUpdate: feedMeta?.updated_at || null,
                docs: [],
              };
            }
            acc[feedId].docs.push(doc);
          });
        } else {
          acc['__ungrouped'] = acc['__ungrouped'] || { docs: [] };
          acc['__ungrouped'].docs.push(doc);
        }
        return acc;
      }, {})
    : null;

  const renderDocumentItem = (doc) => {
    const isSelected = selectedDoc?.id === doc.id;
    const hasThumbnailError = thumbnailErrors[doc.id];

    return (
      <div
        key={doc.id}
        ref={isSelected ? selectedItemRef : null}
        className={`fileItem flex items-start gap-3 p-3 cursor-pointer transition-colors duration-150 ${
          isSelected ? 'bg-blue-50 ring-2 ring-blue-500' : 'hover:bg-gray-100'
        }`}
        onClick={() => !doc.isProcessing && setSelectedDoc(doc)}
        onContextMenu={(e) => {
          if (doc.isProcessing) return;
          e.preventDefault();
          setContextMenu({ visible: true, x: e.pageX, y: e.pageY, doc });
        }}
      >
        {/* Thumbnail or icon */}
        <div className="flex items-center gap-2 mt-1" title={doc.file?.split('.').pop()}>
          <div className="w-6 h-6 overflow-hidden">
            {previewMode === 'icon' ? (
              getFileIcon(doc.file, doc.isProcessing)
            ) : (
              doc.id && !hasThumbnailError && !doc.isProcessing ? (
                <img
                  src={`${API_URL}/api/documents/${doc.id}/thumbnail`}
                  alt="Document thumbnail"
                  className="w-64 h-auto rounded border shadow-sm"
                  onError={() => setThumbnailErrors(prev => ({ ...prev, [doc.id]: true }))}
                />
              ) : (
                getFileIcon(doc.file, doc.isProcessing)
              )
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col overflow-hidden">
          <h3 className="text-base font-medium truncate flex items-center flex-wrap space-x-2">
            {doc.title}
            {doc.isProcessing && <span className="ml-2 text-sm text-blue-400">(processing)</span>}
          </h3>
          <p className="text-sm text-gray-500 line-clamp-3">{doc.project_based_summary || doc.summary}</p>
        </div>

        <button
          className="context-menu-button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setContextMenu(prev => ({ ...prev, visible: !prev.visible, doc, x: 10, y: 10 }));
          }}
        >
          ...
        </button>
      </div>
    );
  };

  /* const feedGroups = useMemo(() => {
  if (!groupByFeed) return null;

  const groups = {};
    documents.forEach(doc => {
      if (doc.feed_id) {
        if (!groups[doc.feed_id]) {
          groups[doc.feed_id] = {
            feed: { id: doc.feed_id, title: doc.feed_title },
            docs: [],
          };
        }
        groups[doc.feed_id].docs.push(doc);
      }
    });
    return groups;
  }, [documents, groupByFeed]); */

  return (
    <>
    <div className="min-h-screen bg-gray-50 relative">
      <div className="flex justify-between items-center p-4 border-b">
        <div className="text-xl font-semibold product-title"><ElverdonLogo /> <span className="branding">Verriton</span> <em>Atlas</em> {/* <span className="text-sm font-thin">Comprehend policy. Empowering society.</span> */}</div>
        <Menu as="div" className="relative inline-block text-left">
          <Menu.Button className="inline-flex items-center gap-2 hover:bg-gray-200 px-3 py-1 rounded-full">
            <UserCircleIcon className="w-5 h-5 text-gray-600" />
            <span className="text-sm">{user.email}</span>
            <ChevronDownIcon className="w-4 h-4" />
          </Menu.Button>
          <Menu.Items className="bg-white absolute right-0 mt-2 w-48 origin-top-right border border-gray-200 divide-y divide-gray-100 rounded-md shadow-lg focus:outline-none z-50">
            <div className="py-1">
              <Menu.Item>
                <span className="block w-full text-right px-4 py-2 text-sm text-gray-500">
                  ❤︎ Thanks for using Verrinton Atlas.
                </span>
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => setActiveDialog('project')}
                    className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700`}
                  >
                    Create New Dossier
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => setActiveDialog('upload')}
                    className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700`}
                  >
                    Upload Document / URL
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => setActiveDialog('newsletter')}
                    className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700`}
                  >
                    Import Newsletter Feed
                  </button>
                )}
              </Menu.Item>
            </div>
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={onLogout}
                    className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-4 py-2 text-sm text-red-700`}
                  >
                    Log out
                  </button>
                )}
              </Menu.Item>
            </div>
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => alert('About this app')}
                    className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700`}
                  >
                    About Verriton
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Menu>
      </div>

      <div className="relative h-[calc(100vh-60px)] flex">
        {/* Dossier Chat Sidebar */}
        <div className={`flex-shrink-0 border-r bg-white flex flex-col transition-all duration-200 ${showChat ? 'w-72' : 'w-10'}`}>
          <button
            onClick={() => setShowChat(v => !v)}
            className="p-2 text-gray-500 hover:text-blue-600 flex items-center gap-1 text-xs border-b"
            title={showChat ? 'Hide dossier chat' : 'Talk to the dossier'}
          >
            <MessagesSquare className="w-4 h-4" />
            {showChat && <span>◀</span>}
          </button>
          {showChat && (
            <div className="flex-1 overflow-hidden">
              <DossierChat
                projectId={selectedCollection}
                projectTitle={currentProject?.title}
              />
            </div>
          )}
        </div>

        <div className="flex-1 flex flex-col min-w-0">
        { <ProjectTabs
            collections={[...collections, { id: 'all', title: 'All Documents', objective: '-' }]}
            setCollections={setCollections}
            selectedCollection={selectedCollection}
            setSelectedCollection={setSelectedCollection}
            onOpenProjectDialog={() => setActiveDialog('project')}
          /> }
        { selectedCollection !== 'all' && (
          <>
          { showInsight && (
            
            <div className="p-4 border-b flex justify-between items-start">
              {selectedCollection && (
              <>
                <div className="grid grid-cols-[max-content,1fr] gap-x-4 gap-y-2 text-sm text-gray-700">
                  <div className="font-semibold">Dossier objective:</div>
                  <div>{currentProject?.objective || '—'}</div>
                </div>
              </>) }
              <div className="flex justify-end items-center space-x-2">
                {/* Delete button - left of the other button */}
                {currentProject && (
                  <button
                    onClick={async () => {
                      const confirmed = window.confirm(`Delete dossier "${currentProject.title}"?`);
                      if (!confirmed) return;

                      try {
                        const res = await axios.delete(`${API_URL}/api/projects/${currentProject.id}`);

                        toast.success('Dossier deleted');
                        setSelectedCollection(null); // clear current selection
                        await fetchProjects();    // refresh project list
                      } catch (err) {
                        console.error(err);
                        toast.error('Error deleting dossier');
                      }
                    }}
                    className="text-red-600 hover:text-red-800 flex items-center space-x-1"
                    title="Delete this dossier"
                  >
                    <TrashIcon className="w-5 h-5" />
                    <span className="text-sm"></span>
                  </button>
                )}

                {/* Your existing top-right button follows here */}
                <button
                  onClick={() => setShowInsight(false)}
                  className="text-gray-500 hover:text-gray-700 text-xs ml-4"
                >
                  ▲
                </button>
              </div>              
              
            </div>
          )}
          {!showInsight && (
            <div className="border-b text-xs text-gray-500 px-4 py-2 cursor-pointer hover:text-gray-700 text-right" onClick={() => setShowInsight(true)}>
              Show dossier details ◀
            </div>
          )}
          </>       
        )}

        <Split
          className="flex flex-1"
          sizes={selectedDoc ? [60, 40] : [100, 0]}
          minSize={selectedDoc ? [200, 300] : [200, 0]}
          gutterSize={6}
          expandToMin={false}
        >
          {/* Left panel */}
          <div className="p-4 overflow-y-auto bg-gray-50">
            <div className="mb-4 flex gap-4">
              <div className="relative w-full">
                <input
                    type="text"
                    placeholder="Search documents..."
                    className="border rounded px-3 py-1 w-full pr-24"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <label className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-gray-400 flex items-center gap-1 px-2">
                    <input
                    type="checkbox"
                    checked={useNaturalLanguage}
                    onChange={e => setUseNaturalLanguage(e.target.checked)}
                    className="accent-gray-400 w-3 h-3"
                    />
                    <span className="hidden sm:inline">Strict search</span>
                </label>
                </div>
              <div className="flex gap-0">
                <button
                  onClick={() => setPreviewMode(prev => prev === 'icon' ? 'preview' : 'icon')}
                  className="px-2 py-1 rounded text-sm hover:bg-gray-100"
                >
                  {previewMode === 'icon' ? '📄' : '🎑'}
                </button>
                <button
                  onClick={() => setListMode(prev => {
                    const modes = ['compact', 'list', 'table'];
                    const nextIndex = (modes.indexOf(prev) + 1) % modes.length;
                    return modes[nextIndex];
                  })}
                  className="px-2 py-1 rounded text-sm hover:bg-gray-100"
                >
                  { modeIconMap[listMode]}
                </button>
                <button onClick={() => setGroupByFeed(!groupByFeed)}>
                  {groupByFeed ? <FaRegFile className="text-black w-4 h-4" /> : <FaRss className="text-black w-4 h-4" /> }
                </button>
              </div>
              <select className="border rounded px-3 py-1" value={sortBy} 
                onClick={e => {
                  const selected = e.target.value;
                  if (selected === sortBy) {
                    toggleSort(sortBy);
                  }
                }}
                onChange={e => {
                  const selected = e.target.value;
                  if (selected === sortBy) {
                    toggleSort(sortBy);
                  } else {
                    setSortBy(selected);
                    setSortDirection('desc');
                  }
                }}>
                {sortOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label} { /* sortBy === opt.value && (sortDirection === 'ASC' ? '↑' : '↓') */ }
                  </option>
                ))}
              </select>
            </div>

            {groupByFeed && (
              <div className="space-y-2 grid grid-cols-1 gap-3 p-2 ">
                {Object.entries(groupedFeeds)
                 .filter(([feedId]) => feedId !== '__ungrouped')
                 .map(([feedId, group]) => (
                  group.feed_title !== "__ungrouped" && (
                  <div key={feedId} className={`${expandedFeeds[feedId] ? "border-b border-l border-gray-200 rounded-bl" : ""}`}>
                    <div
                      className="fileItem flex items-start gap-3 p-3 cursor-pointer transition-colors duration-150 hover:bg-gray-100"
                      onClick={() => setExpandedFeeds(prev => ({ ...prev, [feedId]: !prev[feedId] }))}
                    >
                      <div className="flex flex-col overflow-hidden">
                        <div className="flex items-center gap-2 mt-1" title="rss">
                          <div className="w-6 h-6 overflow-hidden">
                            <FaRssSquare className="text-orange-500 w-6 h-6" />
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col flex-1 w-full overflow-hidden">
                        <h3 className="text-base font-medium truncate flex items-center flex-wrap space-x-2">
                          {group.feed_title}
                        </h3>
                        <p className="text-xs text-gray-400">
                          <span className="text-xs text-gray-500">Feed</span> 
                          {group.feed_url && (<> · <a href={group.feed_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline">
                              {group.feed_url}
                            </a>
                            </>
                          )}
                          {group.feed_lastUpdate && ( 
                            <> · <span className="text-xs text-gray-500">{new Intl.DateTimeFormat().format(new Date(group.feed_lastUpdate))}</span>
                            </>
                          )}
                        </p>
                      </div>
                      <span className="text-xs text-gray-500">{group.docs.length} docs</span>
                      
                    </div>

                    {expandedFeeds[feedId] && (
                      <div className="relative pl-6 py-2">
                        

                        {group.docs.map((doc, idx) => (
                          <div key={doc.id} className="relative">
                            {/* Horizontal connector from vertical line to document */}
                            <div className="absolute top-7 -left-6 w-6 h-px border-b border-gray-200"></div>
                            {/* Render document */}
                            <div className="pl-2">{renderDocumentItem(doc)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )))}

                {/* Show ungrouped docs if any */}
                {groupedFeeds?.__ungrouped?.docs?.map(doc => renderDocumentItem(doc))}
              </div>
            )}

            {!groupByFeed && (listMode === 'table' ? (
              <table className="w-full text-sm table-auto border-collapse">
                <thead>
                  <tr className="bg-gray-100 text-left">
                    <th className="px-2 py-1 border"></th>
                    <th className="px-2 py-1 border">Title</th>
                    { selectedCollection !== 'all' && (
                      <>
                        <th className="px-2 py-1 border">Dossier-related Summary</th>
                        <th className="px-2 py-1 border">Relevancy</th>
                        <th className="px-2 py-1 border">Urgency</th>
                      </>
                    )}
                    <th className="px-2 py-1 border">{ selectedCollection !== 'all' && ( "General "  ) }Summary</th>
                    <th className="px-2 py-1 border">Type</th>
                    <th
                      className="px-2 py-1 border cursor-pointer"
                    >
                      Size
                    </th>
                    <th
                      className="px-2 py-1 border cursor-pointer"
                      onClick={() => toggleSort('document_date')}
                    >
                      Document Date {sortBy === 'document_date' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th
                      className="px-2 py-1 border cursor-pointer"
                      onClick={() => toggleSort('retrieval_date')}
                    >
                      Retrieval Date {sortBy === 'retrieval_date' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="px-2 py-1 border">Categories</th>
                    <th className="px-2 py-1 border"></th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map(doc => {
                    const hasThumbnailError = thumbnailErrors[doc.id];
                    return (
                      <tr
                        key={doc.id}
                        className={`cursor-pointer hover:bg-gray-100 ${selectedDoc?.id === doc.id ? 'bg-blue-50' : ''}`}
                        onClick={() => !doc.isProcessing && setSelectedDoc(doc)}
                      >
                        <td className="px-2 py-1 border overflow-hidden">
                          {previewMode === 'icon' ? (
                            getFileIcon(doc.file, doc.isProcessing)
                          ) : (
                            doc.id && !hasThumbnailError && !doc.isProcessing ? (
                                <img
                                src={`${API_URL}/api/documents/${doc.id}/thumbnail`}
                                alt="Document thumbnail"
                                className="w-48 h-auto rounded border shadow-sm max-h-[128px] w-auto"
                                onError={() => setThumbnailErrors(prev => ({ ...prev, [doc.id]: true }))}
                                />
                            ) : (
                              getFileIcon(doc.file, doc.isProcessing)
                            )
                          )}
                        </td>
                        <td className="px-2 py-1 border whitespace-normal break-words">{doc.title}</td>
                        { selectedCollection !== 'all' && (
                          <>
                            <td className="px-2 py-1 border whitespace-normal break-words">{ doc.project_based_summary }</td>
                            <td className="px-2 py-1 border whitespace-normal break-words" title={`Relevancy: ${doc.project_relevancy}/10`}>
                              <div className="flex items-center space-x-1">
                                  {[...Array(10)].map((_, i) => (
                                    <div
                                      key={i}
                                      className={`w-1.5 h-1.5 rounded-full ${i < doc.project_relevancy ? 'bg-blue-500' : 'bg-gray-200'}`}
                                    />
                                  ))}
                              </div>
                            </td>
                            <td className="px-2 py-1 border whitespace-normal break-words" title={`Urgency: ${doc.project_urgency}/10`}>
                              <div className="flex items-center space-x-1">
                                  {[...Array(10)].map((_, i) => (
                                    <div
                                      key={i}
                                      className={`w-1.5 h-1.5 rounded-full ${i < doc.project_urgency ? 'bg-red-500' : 'bg-gray-200'}`}
                                    />
                                  ))}
                              </div>
                            </td>
                          </>
                        )}
                        <td className="px-2 py-1 border whitespace-normal break-words">{ doc.summary }</td>
                        <td className="px-2 py-1 border">{doc.file?.split('.').pop()}</td>
                        <td className="px-2 py-1 border">{formatFileSize(doc.file_size, false)}</td>
                        <td className="px-2 py-1 border">
                          {doc.document_date ? new Intl.DateTimeFormat(navigator.language, {
                            dateStyle: 'medium',
                          }).format(new Date(doc.document_date)) : '–'}
                        </td>
                        <td className="px-2 py-1 border">
                          {doc.retrieval_date ? new Intl.DateTimeFormat(navigator.language, {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          }).format(new Date(doc.retrieval_date)) : '–'}
                        </td>
                        <td className="px-2 py-1 border whitespace-normal break-words">
                          {doc.categories?.join(', ') || '–'}
                        </td>
                        <td className="px-2 py-1 border">
                          <button class="context-menu-button-in-table" onClick={(e) => {
                            e.preventDefault();       // Prevent default browser behavior
                            e.stopPropagation();      // Prevent bubbling up to parent click handlers
                            setContextMenu(prev => ({...prev, visible: !prev.visible, doc: doc, x: e.pageX - 250, y: e.pageY}))
                          }}>...</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="grid grid-cols-1 gap-3 p-2 fileList">
                {documents.map(doc => {
                    const isSelected = selectedDoc?.id === doc.id;
                    const hasThumbnailError = thumbnailErrors[doc.id];
                    return (
                    <div
                        key={doc.id}
                        ref={isSelected ? selectedItemRef : null}
                        className={`fileItem flex items-start gap-3 p-3 cursor-pointer transition-colors duration-150 ${
                            selectedDoc?.id === doc.id ? 'bg-blue-50 ring-2 ring-blue-500' : 'hover:bg-gray-100'
                        }`}
                        onClick={() => !doc.isProcessing && setSelectedDoc(doc)}
                        onContextMenu={(e) => {
                            if (doc.isProcessing) {
                                return;
                            }
                            e.preventDefault();
                            setContextMenu({ visible: true, x: e.pageX, y: e.pageY, doc });
                        }}
                        >
                        {/* File icon */}
                        <div className="flex items-center gap-2 mt-1" title={doc.file?.split('.').pop()}>
                          <div className="w-6 h-6 overflow-hidden">
                            {previewMode === 'icon' ? (
                              getFileIcon(doc.file, doc.isProcessing)
                            ) : (
                              doc.id && !hasThumbnailError && !doc.isProcessing ? (
                                  <img
                                  src={`${API_URL}/api/documents/${doc.id}/thumbnail`}
                                  alt="Document thumbnail"
                                  className="w-64 h-auto rounded border shadow-sm"
                                  onError={() => setThumbnailErrors(prev => ({ ...prev, [doc.id]: true }))}
                                  />
                              ) : (
                                getFileIcon(doc.file, doc.isProcessing)
                              )
                            )}
                          </div>
                        </div>

                        {/* Document content */}
                        <div className="flex flex-col overflow-hidden">
                            <h3 className="text-base font-medium truncate flex items-center flex-wrap space-x-2">
                              {doc.title} 
                              {doc.isProcessing && <span className="ml-2 text-sm text-blue-400">(processing)</span>} 
                              {/* Relevancy dots */}
                              { doc.project_relevancy > 0 && (
                              <span className="ml-2 flex space-x-0.5" title={`Relevancy: ${doc.project_relevancy}/10`}>
                                {[...Array(10)].map((_, i) => (
                                  <div
                                    key={i}
                                    className={`w-1.5 h-1.5 rounded-full ${i < doc.project_relevancy ? 'bg-blue-500' : 'bg-gray-200'}`}
                                  />
                                ))}
                              </span>
                              )}

                              {/* Urgency dots */}
                              { doc.project_relevancy > 3 && doc.project_urgency > 0 && (
                              <span className="ml-2 flex space-x-0.5" title={`Urgency: ${doc.project_urgency}/10`}>
                                {[...Array(10)].map((_, i) => (
                                  <div
                                    key={i}
                                    className={`w-1.5 h-1.5 rounded-full ${i < doc.project_urgency ? 'bg-red-500' : 'bg-gray-200'}`}
                                  />
                                ))}
                              </span>
                              )}
                            </h3>
                            <p className="text-sm text-gray-500 line-clamp-3">{doc.project_based_summary || doc.summary}</p>
                            { listMode === 'list' && ( <><p className="text-xs text-gray-400"><span>{doc.document_date
                                ? new Intl.DateTimeFormat(navigator.language, {
                                    dateStyle: 'medium',
                                    timeStyle: 'short',
                                }).format(new Date(doc.document_date))
                                : '–'}</span> · <span>{doc.file?.split('.').pop()}</span> · <span>{formatFileSize(doc.file_size, false)}</span>
                            </p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {doc?.categories?.map(cat => (
                                <button
                                  key={cat}
                                  className="bg-blue-100 text-blue-800 px-2 py-0.5 text-xs rounded-full hover:bg-blue-200 focus:outline-none"
                                  onClick={(e) => {
                                    e.stopPropagation(); // prevent triggering document selection
                                    if (!search.includes(`category:${cat}`)) {
                                      setSearch(prev => (prev.trim() ? `${prev} category:${cat}` : `category:${cat}`));
                                    }
                                  }}
                                  title={`Filter by ${cat}`}
                                >
                                  {cat}
                                </button>
                              ))}
                              {!doc.isProcessing && (
                                <button
                                  className="bg-gray-200 text-gray-700 px-2 py-0.5 text-xs rounded-full hover:bg-gray-300 ml-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const newCategory = prompt('Add a new category');
                                    if (newCategory) {
                                      axios.post(`${API_URL}/api/documents/${doc.id}/add-category`, { category: newCategory }, { withCredentials: true })
                                        .then(res => {
                                          // Update the local state with new categories
                                          setDocuments(prev => prev.map(d =>
                                            d.id === doc.id ? { ...d, categories: res.data.categories } : d
                                          ));
                                        })
                                        .catch(() => alert('Failed to add category'));
                                    }
                                  }}
                                >
                                  ＋
                                </button>
                              )}                            
                            </div> </>)}
                        </div>
                        <button class="context-menu-button" onClick={(e) => {
                          e.preventDefault();       // Prevent default browser behavior
                          e.stopPropagation();      // Prevent bubbling up to parent click handlers
                          setContextMenu(prev => ({...prev, visible: !prev.visible, doc: doc, x: 10, y: 10}))
                        }}>...</button>
                    </div>
                    )
                })}
              </div>
            ))}
          </div>

          <div className={`side-panel bg-white shadow-lg border-l border-gray-200 flex flex-col z-10 ${selectedDoc ? 'block' : 'hidden'}`}>
            {selectedDoc ? (
                <>
                <div className="flex justify-between items-center border-b px-4 py-2">
                    <h2 className="text-lg font-semibold">Document Details</h2>
                    <div className="flex gap-2 items-center">
                    <button
                        onClick={() => setShowCommentPanel(v => !v)}
                        className={`inline-flex items-center gap-1 text-sm px-2 py-1 border rounded transition-colors ${
                            showCommentPanel
                            ? 'text-white bg-amber-500 border-amber-500 hover:bg-amber-600'
                            : docNoteCount > 0
                            ? 'text-amber-600 bg-amber-50 border-amber-400 hover:bg-amber-100'
                            : 'text-gray-500 border-gray-300 hover:text-gray-700 hover:border-gray-400'
                        }`}
                        title="Toggle comments panel"
                    >
                        <MessageSquarePlus className="w-4 h-4" />
                        {docNoteCount > 0 && <span>{docNoteCount}</span>}
                    </button>
                    <button
                        onClick={() => setShowViewer(prev => !prev)}
                        className={`flex items-center gap-1 text-sm px-2 py-1 border rounded transition-colors ${
                            showViewer
                            ? 'text-white bg-blue-600 border-blue-600 hover:bg-blue-700'
                            : 'text-blue-600 border-blue-500 hover:text-blue-800 hover:border-blue-600'
                        }`}
                        title="Toggle Document Viewer"
                        aria-label="Toggle Document Viewer"
                        >
                        {showViewer ? (
                            <>
                            <EyeSlashIcon className="w-4 h-4" />
                            Hide
                            </>
                        ) : (
                            <>
                            <EyeIcon className="w-4 h-4" />
                            View
                            </>
                        )}
                    </button>                       
                    <button
                        onClick={handleDelete}
                        className="text-red-600 hover:text-red-800 text-sm px-2 py-1 border border-red-500 rounded"
                        title="Delete Document"
                        aria-label="Delete Document">
                        <TrashIcon className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => {
                            setSelectedDoc(null);
                            setShowViewer(false);
                        }}
                        className="text-gray-500 hover:text-red-500 text-xl"
                        aria-label="Close"
                    >
                        &times;
                    </button>
                    </div>
                </div>
                <div className="overflow-auto p-4 text-sm mb-10">
                    {showViewer && (
  <>
    {/* Tabs */}
    <div className="flex mb-2 space-x-2">
      { !selectedDoc.file?.split('.').pop().match(/(epub)/) ? (
        <button
          className={`px-3 py-1 rounded text-sm font-medium ${
            viewerTab === 'preview' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
          }`}
          onClick={() => {
            if (viewerTab === 'preview') {
              setShowViewer(false);
            } else {
              setShowViewer(true);
              setViewerTab('preview');
            }
          }}
        >
          Preview
        </button>
      ) : viewerTab !== 'transcript' && setViewerTab("transcript") }
      <button
        className={`px-3 py-1 rounded text-sm font-medium ${
          viewerTab === 'transcript' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
        }`}
        onClick={() => {
          if (viewerTab === 'transcript') {
            setShowViewer(false);
          } else {
            setShowViewer(true);
            setViewerTab('transcript');
          }
        }}
      >
        Transcript
      </button>
    </div>

    {/* Preview Tab */}
    {viewerTab === 'preview' && (
      <>
        {selectedDoc.file?.split('.').pop().match(/(xml|rss|atom)/) && feed?.items && (
          <div className="mb-4 border rounded p-4 bg-white">
            <h3 className="text-lg font-semibold mb-2">{ feed.meta.title }</h3>
            {feed.items.length === 0 && (
              <p className="text-sm text-gray-500">No items found or failed to parse.</p>
            )}
            <ul className="space-y-2">
              {feed.items.map((item, idx) => (
                <li key={idx} className="text-sm border-b pb-2">
                  <div className="text-gray-500 text-xs">{item.pubDate}</div>
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    {item.title}
                  </a>
                  <div className="text-gray-500 text-xs">{item.description}</div>
                </li>
              ))}
            </ul>
            <ul className="mt-5 mb-5">
              {Object.entries(feed.meta).map(([key, value]) => (
                <li key={key}>
                  <strong>{key}:</strong> {value}
                </li>
              ))}
            </ul>
          </div>
        )}
        {selectedDoc.file?.split('.').pop().match(/(mp3|wav|m4a|aac|flac)/) && (
          <div className="mb-4">
            <audio ref={audioRef} controls className="w-full">
              <source src={`${API_URL}/api/documents/${selectedDoc.id}/download`} />
              Your browser does not support the audio element.
            </audio>
          </div>
        )}

        {selectedDoc.file?.split('.').pop().match(/(mp4|mov|webm|mkv)/) && (
          <div className="mb-4">
            <video ref={videoRef} controls className="w-full">
              <source src={`${API_URL}/api/documents/${selectedDoc.id}/download`} />
              Your browser does not support the video element.
            </video>
          </div>
        )}

        {/* selectedDoc.file?.split('.').pop() === 'epub' && (
          <div className="mb-4 border rounded overflow-hidden">
            <div id="epub-reader" className="w-full h-[500px] border rounded" />
          </div>
        ) */}

        {!selectedDoc.file?.split('.').pop().match(/(rss|xml|atom|mp3|wav|m4a|aac|flac|mp4|mov|webm|mkv|epub)/) && (
          <div className="mb-4 border rounded overflow-hidden">
            <iframe
              src={`${API_URL}/api/documents/${selectedDoc.id}/view`}
              title="Document Viewer"
              className="w-full h-[300px] border-none"
            />
          </div>
        )}
      </>
    )}

    {/* Transcript Tab */}
    {viewerTab === 'transcript' && (
      <div className="mb-4 border rounded overflow-hidden bg-gray-50 text-sm text-gray-800 p-4 whitespace-pre-wrap">
        <iframe
          src={`${API_URL}/api/documents/${selectedDoc.id}/transcript`}
          title="Transcript"
          className="w-full h-[300px] border-none"
        />
      </div>
    )}
  </>
)}

                    <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 items-start">
                        <div className="text-right font-medium">Preview:</div>
                        <div>
                          {selectedDoc?.id && !thumbnailErrors[selectedDoc.id] ? (
                            <img
                              src={`${API_URL}/api/documents/${selectedDoc.id}/thumbnail`}
                              alt="Document thumbnail"
                              className="w-48 h-auto rounded border shadow-sm"
                              onError={() => setThumbnailErrors(prev => ({ ...prev, [selectedDoc.id]: true }))}
                            />
                          ) : (
                            '–'
                          )}
                        </div>

                        <div className="text-right font-medium">Title:</div>
                        <div className="flex items-start gap-1 flex-wrap">
                          {editingField === 'title' ? (
                            <span className="flex items-center gap-1 flex-1">
                              <input autoFocus className="border rounded px-2 py-0.5 text-sm flex-1" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleFieldSave('title'); if (e.key === 'Escape') setEditingField(null); }} />
                              <button onClick={() => handleFieldSave('title')} className="text-green-600 hover:text-green-800"><Check className="w-4 h-4" /></button>
                              <button onClick={() => setEditingField(null)} className="text-gray-400 hover:text-gray-600"><XIcon className="w-4 h-4" /></button>
                            </span>
                          ) : (
                            <>
                              {selectedDoc.title}
                              <button onClick={() => { setEditingField('title'); setEditValue(selectedDoc.title || ''); }} className="text-gray-300 hover:text-blue-500 ml-1" title="Edit title"><Pencil className="w-3.5 h-3.5" /></button>
                            </>
                          )}
                          <FieldNote entityType="document" entityId={selectedDoc.id} fieldKey="title" label="Title" preloadedCount={fieldNoteCounts['title'] || 0} onCountChange={(fk, c) => setFieldNoteCounts(prev => ({ ...prev, [fk]: c }))} />
                        </div>

                        {currentProject && (
                          <>
                            <div className="text-right font-medium">Dossier-related<br/> summary for <br/>{currentProject?.title}:</div>
                            <div className="flex items-start gap-1 flex-wrap">
                              {editingField === 'project_based_summary' ? (
                                <span className="flex items-start gap-1 flex-1">
                                  <textarea autoFocus rows={3} className="border rounded px-2 py-0.5 text-sm flex-1 resize-y" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={e => { if (e.key === 'Escape') setEditingField(null); }} />
                                  <span className="flex flex-col gap-1">
                                    <button onClick={() => handleFieldSave('project_based_summary')} className="text-green-600 hover:text-green-800"><Check className="w-4 h-4" /></button>
                                    <button onClick={() => setEditingField(null)} className="text-gray-400 hover:text-gray-600"><XIcon className="w-4 h-4" /></button>
                                  </span>
                                </span>
                              ) : (
                                <>
                                  {selectedDoc.project_based_summary}
                                  <button onClick={() => { setEditingField('project_based_summary'); setEditValue(selectedDoc.project_based_summary || ''); }} className="text-gray-300 hover:text-blue-500 ml-1 flex-shrink-0" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                                </>
                              )}
                              <FieldNote entityType="document" entityId={selectedDoc.id} fieldKey="project_based_summary" label="Dossier summary" preloadedCount={fieldNoteCounts['project_based_summary'] || 0} onCountChange={(fk, c) => setFieldNoteCounts(prev => ({ ...prev, [fk]: c }))} />
                            </div>
                          </>
                        )}

                        {currentProject && (
                          <>
                            <div className="text-right font-medium">Dossier relevancy:</div>
                            <div>{selectedDoc.project_relevancy === 0 ? "–" : selectedDoc.project_relevancy + "/10"}</div>
                          </>
                        )}

                        {currentProject && (
                          <>
                            <div className="text-right font-medium">Dossier urgency:</div>
                            <div>{selectedDoc.project_urgency === 0 ? "–" : selectedDoc.project_urgency + "/10"}</div>
                          </>
                        )}

                        <div className="text-right font-medium">Summary:</div>
                        <div className="flex items-start gap-1 flex-wrap">
                          {editingField === 'summary' ? (
                            <span className="flex items-start gap-1 flex-1">
                              <textarea autoFocus rows={3} className="border rounded px-2 py-0.5 text-sm flex-1 resize-y" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={e => { if (e.key === 'Escape') setEditingField(null); }} />
                              <span className="flex flex-col gap-1">
                                <button onClick={() => handleFieldSave('summary')} className="text-green-600 hover:text-green-800"><Check className="w-4 h-4" /></button>
                                <button onClick={() => setEditingField(null)} className="text-gray-400 hover:text-gray-600"><XIcon className="w-4 h-4" /></button>
                              </span>
                            </span>
                          ) : (
                            <>
                              {selectedDoc.summary}
                              <button onClick={() => { setEditingField('summary'); setEditValue(selectedDoc.summary || ''); }} className="text-gray-300 hover:text-blue-500 ml-1 flex-shrink-0" title="Edit summary"><Pencil className="w-3.5 h-3.5" /></button>
                            </>
                          )}
                          <FieldNote entityType="document" entityId={selectedDoc.id} fieldKey="summary" label="Summary" preloadedCount={fieldNoteCounts['summary'] || 0} onCountChange={(fk, c) => setFieldNoteCounts(prev => ({ ...prev, [fk]: c }))} />
                        </div>

                        <div className="text-right font-medium">Author:</div>
                        <div className="flex items-center gap-1 flex-wrap">
                          {editingField === 'author' ? (
                            <span className="flex items-center gap-1 flex-1">
                              <input autoFocus className="border rounded px-2 py-0.5 text-sm flex-1" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleFieldSave('author'); if (e.key === 'Escape') setEditingField(null); }} />
                              <button onClick={() => handleFieldSave('author')} className="text-green-600 hover:text-green-800"><Check className="w-4 h-4" /></button>
                              <button onClick={() => setEditingField(null)} className="text-gray-400 hover:text-gray-600"><XIcon className="w-4 h-4" /></button>
                            </span>
                          ) : (
                            <>
                              {selectedDoc.author || '–'}
                              <button onClick={() => { setEditingField('author'); setEditValue(selectedDoc.author || ''); }} className="text-gray-300 hover:text-blue-500 ml-1" title="Edit author"><Pencil className="w-3.5 h-3.5" /></button>
                            </>
                          )}
                          <FieldNote entityType="document" entityId={selectedDoc.id} fieldKey="author" label="Author" preloadedCount={fieldNoteCounts['author'] || 0} onCountChange={(fk, c) => setFieldNoteCounts(prev => ({ ...prev, [fk]: c }))} />
                        </div>

                        <div className="text-right font-medium">Organization:</div>
                        <div className="flex items-center gap-1 flex-wrap">
                          {editingField === 'organization' ? (
                            <span className="flex items-center gap-1 flex-1">
                              <input autoFocus className="border rounded px-2 py-0.5 text-sm flex-1" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleFieldSave('organization'); if (e.key === 'Escape') setEditingField(null); }} />
                              <button onClick={() => handleFieldSave('organization')} className="text-green-600 hover:text-green-800"><Check className="w-4 h-4" /></button>
                              <button onClick={() => setEditingField(null)} className="text-gray-400 hover:text-gray-600"><XIcon className="w-4 h-4" /></button>
                            </span>
                          ) : (
                            <>
                              {selectedDoc.organization || '–'}
                              <button onClick={() => { setEditingField('organization'); setEditValue(selectedDoc.organization || ''); }} className="text-gray-300 hover:text-blue-500 ml-1" title="Edit organization"><Pencil className="w-3.5 h-3.5" /></button>
                            </>
                          )}
                          <FieldNote entityType="document" entityId={selectedDoc.id} fieldKey="organization" label="Organization" preloadedCount={fieldNoteCounts['organization'] || 0} onCountChange={(fk, c) => setFieldNoteCounts(prev => ({ ...prev, [fk]: c }))} />
                        </div>

                        <div className="text-right font-medium">Document Date:</div>
                        <div>
                        {selectedDoc.document_date
                            ? new Intl.DateTimeFormat(navigator.language, {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                            }).format(new Date(selectedDoc.document_date))
                            : '–'}
                        </div>

                        <div className="text-right font-medium">Retrieved:</div>
                        <div>
                        {selectedDoc.retrieval_date
                            ? new Intl.DateTimeFormat(navigator.language, {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                            }).format(new Date(selectedDoc.retrieval_date))
                            : '–'}
                        </div>

                        <div className="text-right font-medium">Categories:</div>
                        <div className="flex flex-wrap gap-2 items-center">
                          <FieldNote entityType="document" entityId={selectedDoc.id} fieldKey="categories" label="Categories" preloadedCount={fieldNoteCounts['categories'] || 0} onCountChange={(fk, c) => setFieldNoteCounts(prev => ({ ...prev, [fk]: c }))} />
                          {selectedDoc?.categories?.map(cat => (
                            <button
                              key={cat}
                              className="bg-blue-100 text-blue-800 px-2 py-0.5 text-xs rounded-full hover:bg-blue-200 focus:outline-none"
                              onClick={(e) => {
                                e.stopPropagation(); // prevent triggering document selection
                                if (!search.includes(`category:${cat}`)) {
                                  setSearch(prev => (prev.trim() ? `${prev} category:${cat}` : `category:${cat}`));
                                }
                              }}
                              title={`Filter by ${cat}`}
                            >
                              {cat}
                            </button>
                          ))}
                          
                          <button
                            className="bg-gray-200 text-gray-700 px-2 py-0.5 text-xs rounded-full hover:bg-gray-300 ml-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              const newCategory = prompt('Add a new category');
                              if (newCategory) {
                                axios.post(`${API_URL}/api/documents/${selectedDoc.id}/add-category`, { category: newCategory }, { withCredentials: true })
                                  .then(res => {
                                    // Update the local state with new categories
                                    setDocuments(prev => prev.map(d =>
                                      d.id === selectedDoc.id ? { ...d, categories: res.data.categories } : d
                                    ));
                                  })
                                  .catch(() => alert('Failed to add category'));
                              }
                            }}
                          >
                            ＋
                          </button>
                        </div>

                        <div className="text-right font-medium">In Dossiers:</div>
                        <div className="flex flex-wrap gap-2">
                          { selectedDoc?.projects?.length === 0 ? "–" : ((collections ?? [])
                            .filter(col => selectedDoc.projects.includes(col.id))
                            .map(project => (
                              <button
                                key={project.id}
                                onClick={() => setSelectedCollection(project.id)}
                                className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full hover:bg-blue-200"
                              >
                                {project.title}
                              </button>
                            ))
                          ) }
                        </div>

                        <div className="text-right font-medium">In Feeds:</div>
                        <div className="flex flex-wrap gap-2">
                          { selectedFeedTitles === null || selectedFeedTitles?.length === 0 ? "–" : ( (selectedFeedTitles ?? [])
                            .map(feed_title => (
                              <button
                                className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full hover:bg-blue-200"
                              >
                                {feed_title}
                              </button>
                            ))
                          ) }
                        </div>

                        <div className="text-right font-medium">Type:</div>
                        <div>
                            {selectedDoc.file?.split('.').pop()}
                        </div>

                        <div className="text-right font-medium">File Size:</div>
                        <div>{formatFileSize(selectedDoc.file_size)}</div>

                        <div className="text-right font-medium">Word Count:</div>
                        <div>{formatWordCount(selectedDoc.word_count)}</div>
                        
                        <div className="text-right font-medium">URL:</div>
                        <div>
                        {selectedDoc.url ? (
                            <a
                            href={selectedDoc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline break-all"
                            >
                            {selectedDoc.url}
                            </a>
                        ) : (
                            '–'
                        )}
                        </div>
                        
                        <div className="text-right font-medium">DOI:</div>
                        <div>
                        {selectedDoc.doi ? (
                            <a
                            href={formatDOI(selectedDoc.doi)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline break-all"
                            >
                            {selectedDoc.doi}
                            </a>
                        ) : (
                            '–'
                        )}
                        </div>

                        {selectedDoc.file && (
                        <>
                            <div className="text-right font-medium">File:</div>
                            <div className="flex flex-col gap-1">
                            <a
                                href={`${API_URL}/api/documents/${selectedDoc.id}/download`}
                                className="text-blue-600 underline"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Download file
                            </a>
                            <button
                                onClick={() => setShowViewer(true)}
                                className="text-blue-600 underline text-left"
                            >
                                View document
                            </button>
                            </div>
                        </>
                        )}

                        <div className="text-right font-medium">Citations:</div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              className="px-3 py-1 bg-gray-100 text-sm rounded hover:bg-gray-200"
                              onClick={() => copyCitation(selectedDoc, 'RIS')}
                            >
                              Copy RIS
                            </button>
                            <button
                              className="px-3 py-1 bg-gray-100 text-sm rounded hover:bg-gray-200"
                              onClick={() => copyCitation(selectedDoc, 'ENW')}
                            >
                              Copy ENW
                            </button>
                            <button
                              className="px-3 py-1 bg-gray-100 text-sm rounded hover:bg-gray-200"
                              onClick={() => copyCitation(selectedDoc, 'BIB')}
                            >
                              Copy BibTeX
                            </button>
                          </div>
                      </div>
                </div>
                </>
            ) : null}
            </div>
        </Split>
        </div>{/* end flex-1 flex-col */}

        {/* Comment Panel - right of document details */}
        {showCommentPanel && selectedDoc && (
          <DocumentCommentPanel
            documentId={selectedDoc.id}
            onClose={() => setShowCommentPanel(false)}
            onNotesChange={(total) => setDocNoteCount(total)}
          />
        )}
      </div>

      <button
        onClick={() => setActiveDialog('upload')}
        className="fixed bottom-6 right-6 z-50 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-full shadow-lg flex"
      >
        <PlusIcon className="w-5 h-5 font-black inline middle center leading" /> Upload Document / URL
      </button>

      {activeDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-xl">
            
            {/* Modal Header with Tabs */}
            <div className="flex justify-between items-center mb-4 border-b pb-2">
              <div className="flex space-x-4 text-sm font-medium">
                <button
                  className={`px-2 pb-1 ${activeDialog === 'upload' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setActiveDialog('upload')}
                >
                  Upload Document
                </button>
                <button
                  className={`px-2 pb-1 ${activeDialog === 'newsletter' ? 'border-b-2 border-orange-500 text-orange-600' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setActiveDialog('newsletter')}
                >
                  Import Newsletter Feed
                </button>
                <button
                  className={`px-2 pb-1 ${activeDialog === 'project' ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setActiveDialog('project')}
                >
                  Create New Dossier
                </button>
              </div>
              <button onClick={() => setActiveDialog(null)} className="text-gray-500 hover:text-gray-800 text-xl">&times;</button>
            </div>

            {/* Modal Body */}
            <div>
              {activeDialog === 'upload' && (
              <form ref={formRef} onSubmit={handleUpload} className="grid grid-cols-1 gap-4">
                <div
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded px-4 py-6 text-center transition-colors rounded-xl ${
                      newDoc.files ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50'
                      }`}
                  >
                  <p className="text-sm font-semibold text-gray-500 flex items-center justify-center h-full mb-2">
                    <FaFileUpload className="h-5 w-5 pr-2" /> 

                    {Array.isArray(newDoc.files) && newDoc.files && newDoc.files.length > 0 ? `Selected: ${newDoc.files.map(f => f.name).join(', ')}` : 'Drag & drop documents here or click to browse'}
                  </p>
                  <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={e => setNewDoc({ ...newDoc, files: Array.from(e.target.files) })}
                  className="w-full cursor-pointer opacity-0 absolute"
                  style={ isDragging ? { position: 'absolute', inset: 0 } : {}}
                  />
                </div>
                  
                <div className="flex items-center my-4">
                  <hr className="flex-grow border-gray-300" />
                  <span className="mx-4 text-gray-400 text-sm">or</span>
                  <hr className="flex-grow border-gray-300" />
                </div>
                <textarea
                  ref={urlInputRef}
                  placeholder="Paste one or more document URLs here..."
                  value={newDoc.url}
                  rows={4}
                  className="border px-3 py-2 rounded resize-y w-full"
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('Text');
                    if (pasted.includes(' ') && !pasted.includes('\n')) {
                      e.preventDefault();
                      const transformed = pasted.replace(/\s+/g, '\n');
                      const start = e.target.selectionStart;
                      const end = e.target.selectionEnd;
                      const current = e.target.value;
                      const newValue = current.slice(0, start) + transformed + current.slice(end) + " ";
                      setNewDoc({ ...newDoc, url: newValue });
                    }
                    setNewDoc({ ...newDoc, url: newDoc.url.trim() + " " });
                  }}
                  onChange={(e) => setNewDoc({ ...newDoc, url: e.target.value })}
                />
                <div className="text-sm text-gray-400 italic mb-4">
                      You can add details and attributes to this document later directly in the dossier folder.
                  </div>
                <button type="submit" className="bg-blue-600 text-white py-2 px-4 rounded">Upload</button>
              </form>
              )}
              {activeDialog === 'newsletter' && (
                <>
                  <p className="text-sm text-gray-600 mb-3">
                    Use the following unique email address when subscribing to newsletters.  
                    Emails sent to this address will appear in your feed.
                  </p>

                  <div className="relative mb-4">
                    <input
                      type="text"
                      readOnly
                      value="kilo-tango-oscar-sierra@verriton.com"
                      className="border rounded px-3 py-2 w-full pr-10 font-mono text-sm bg-gray-50"
                      onFocus={(e) => e.target.select()} // allows easy copy by selecting
                    />
                    <button
                      onClick={async () => {
                        await navigator.clipboard.writeText(user.uniqueFeedEmail);
                        toast.success('Email copied to clipboard!');
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
                      title="Copy to clipboard"
                    >
                      <FaRegClipboard className="text-black w-4 h-4" />
                    </button>
                  </div>

                  <button
                    onClick={() => setActiveDialog(null)}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 w-full"
                  >
                    Done
                  </button>
                </>
              )}
              {activeDialog === 'project' && (
                <NewProjectDialogContent
                  newProject={newProject}
                  setNewProject={setNewProject}
                  handleCreateProject={handleCreateProject}
                  onClose={() => setActiveDialog(null)}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    {contextMenu.visible && (
        <div
            className="absolute bg-white border rounded shadow-lg z-50 text-sm"
            style={{ top: contextMenu.y, left: contextMenu.x }}
            onClick={() => setContextMenu(prev => ({ ...prev, visible: false }))}
            onContextMenu={(e) => e.preventDefault()}
        >
            <div className="px-3 py-2 border-b text-sm text-gray-800 font-light truncate max-w-[200px]">
                {contextMenu.doc.title}
            </div>
            <button
              className="block w-full px-4 py-2 hover:bg-gray-100 text-left"
              onClick={() => {
                const category = prompt('Add a new category');
                if (!category || !contextMenu.doc) return;
                addCategoryToDoc(contextMenu.doc, category);
                setContextMenu(prev => ({ ...prev, visible: false }));
              }}
            >
              Add Category
            </button>
            <div
              className="relative"
              onMouseEnter={() => setShowProjectSubmenu(true)}
              onClick={(e) => {
                e.stopPropagation();
                setShowProjectSubmenu(!showProjectSubmenu);
              }}
              onMouseLeave={() => setShowProjectSubmenu(false)}
            >
              <button className="block w-full px-4 py-2 hover:bg-gray-100 text-left text-sm flex justify-between items-center">
                <span>{ isMacOrLinux ? "Add To Dossier" : "➕ Add To Dossier" }</span>
                <span className="ml-2">▸</span>
              </button>

              {showProjectSubmenu && (
                <div className="absolute left-full top-0 w-48 bg-white rounded-tr-md rounded-br-md shadow-lg z-50">
                  <div className="py-1 text-xs text-gray-400 px-3">Dossiers</div>
                  {collections.map(project => {
                    const isInProject = contextMenu.doc.projects?.includes(project.id);
                    return (
                      <button
                        key={project.id}
                        className="block w-full px-4 py-1 hover:bg-gray-100 text-left flex text-sm"
                        onClick={async () => {
                          try {
                            const endpoint = `${API_URL}/api/projects/${project.id}/${isInProject ? 'remove' : 'add'}-documents`;
                            await axios.post(endpoint, { documentIds: [contextMenu.doc.id] }, { withCredentials: true });
                            setDocuments(prev =>
                              prev.map(doc =>
                                doc.id === contextMenu.doc.id
                                  ? {
                                      ...doc,
                                      projects: isInProject
                                        ? doc.projects.filter(id => id !== project.id)
                                        : [...(doc.projects || []), project.id]
                                    }
                                  : doc
                              )
                            );
                          } catch (err) {
                            alert('Failed to update project association');
                            console.error(err);
                          }
                          setContextMenu(prev => ({ ...prev, visible: false }));
                        }}
                      >
                        <span className={isInProject ? "mr-2" : ""}>{isInProject ? '✓' : ''}</span> 
                        <span>{project.title}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="border-t px-3 py-2 text-xs text-gray-400">Copy Citations</div>
              {['RIS', 'ENW', 'BIB'].map(format => (
                <button
                  key={format}
                  className="block w-full px-4 py-2 hover:bg-gray-100 text-left"
                  onClick={async () => {
                    copyCitation(selectedDoc, format);
                  }}
                >
                  {isMacOrLinux ? 'Copy '  + (format === "BIB" ? "BibTeX" : "." + format.toUpperCase()) : '📋 Copy ' + (format === "BIB" ? "BibTeX" : "." + format.toUpperCase()) +  ' Citation' }
                  
                </button>
              ))}
            { true && (
              <button
                className={ "block w-full px-4 py-2 hover:bg-gray-100 text-left " + (contextMenu.doc?.doi ? " ": " text-gray-400 cursor-not-allowed")}
                onClick={async () => {
                  if (contextMenu.doc.doi) {
                    const doi = contextMenu.doc.doi;
                    await navigator.clipboard.writeText(doi);
                    alert('DOI copied to clipboard');
                  }
                }}
              >
                {contextMenu.doc?.doi ?
                 ( isMacOrLinux ? "Copy DOI " : "🔗 Copy DOI " ) + contextMenu.doc?.doi :
                 "No DOI available"
                }
              </button>
            )}
            <div className="border-t my-1" />
            <button
              className="block w-full px-4 py-2 hover:bg-gray-100 text-left font-semibold"
              onClick={() => {
                  setSelectedDoc(contextMenu.doc);
                  setContextMenu(prev => ({ ...prev, visible: false }));
              }}
            >
              {isMacOrLinux ? 'Get Info' : '🔧 Properties'}
            </button>
            
            <button
            className="block w-full px-4 py-2 hover:bg-gray-100 text-left"
            onClick={() => {
                window.open(`${API_URL}/api/documents/${contextMenu.doc.id}/download`, '_blank');
                setContextMenu(prev => ({ ...prev, visible: false }));
            }}
            >
            Download
            </button>
            <button
            className="block w-full px-4 py-2 hover:bg-red-100 text-left text-red-600"
            onClick={() => {
                setSelectedDoc(contextMenu.doc);
                handleDelete();
                setContextMenu(prev => ({ ...prev, visible: false }));
            }}
            >
            {isMac ? 'Move to Bin' : 'Delete'}
            </button>
        </div>
        )}
    </>
  );
};

function getFileIcon(filename, isProcessing) {
  const ext = filename?.split('.').pop().toLowerCase();

  if (isProcessing) {
    return <FaFileUpload />;
  }

  switch (ext) {
    case 'epub': return <FaBook className="text-red-500 w-6 h-6" />;
    case 'pdf': return <FaFilePdf className="text-red-500 w-6 h-6" />;
    case 'doc':
    case 'docx': return <FaFileWord className="text-blue-600 w-6 h-6" />;
    case 'pptx':
    case 'ppt': return <FaFilePowerpoint className="text-orange-600 w-6 h-6" />;
    case 'xls':
    case 'xlsx': return <FaFileExcel className="text-green-600 w-6 h-6" />;
    case 'csv': return <FaFileCsv className="text-green-500 w-6 h-6" />;
    case 'rtf':    
    case 'txt': return <FaFileAlt className="text-gray-500 w-6 h-6" />;
    case 'html':
    case 'htm':
    case 'xml': return <FaFileAlt className="text-orange-500 w-6 h-6" />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
    case 'tiff':
    case 'bmp': return <FaFileImage className="text-purple-500 w-6 h-6" />;    
    case 'wav':
    case 'm4a':
    case 'aac':
    case 'ogg':
    case 'mp3': return <FaFileAudio className="text-blue-500 w-6 h-6" />
    case 'mp4':
    case 'mov': return <FaFileVideo className="text-green-500 w-6 h-6" />
    case 'woff2':
    case 'woff3':
    case 'ttf': return <FaFont className="text-gray-400 w-6 h-6" />
    case 'json': 
    case 'js': 
    case 'c': 
    case 'h': 
    case 'jsx': 
    case 'cs': return <FaFileCode className="text-gray-400 w-6 h-6" />
    default: return <FaFile className="text-gray-400 w-6 h-6" />;
  }
}

function formatFileSize(bytes, long = true) {
  if (!bytes) return '–';

  const thresh = 1024;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = bytes;
  let unit = 'B';

  for (let i = 0; i < units.length && size >= thresh; i++) {
    size /= thresh;
    unit = units[i];
  }

  const locale = navigator.language;
  const formattedSize = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }).format(size);

  const formattedBytes = new Intl.NumberFormat(locale).format(bytes);

  if (long) {
    return `${formattedSize} ${unit} (${formattedBytes} bytes)`;
  } else {
    return `${formattedSize} ${unit}`;
  }
}

function formatWordCount(count) {
  if (!count) return '–';
  return new Intl.NumberFormat(navigator.language).format(count);
}

function shortenUrl(url) {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    const base = `${parsed.protocol}//${parsed.host}`;
    const pathname = parsed.pathname;

    if (pathname.length < 20) return url;

    const parts = pathname.split('/');
    const lastPart = parts.pop();
    return `${base}/.../${lastPart}`;
  } catch {
    return url.length > 40 ? url.slice(0, 20) + '...' + url.slice(-10) : url;
  }
}

function formatDOI(doi) {
  if (!doi) return null;

  // Trim and normalize
  doi = doi.trim();

  if (doi.toLowerCase().startsWith('doi:')) {
    return 'https://doi.org/' + doi.slice(4).trim();
  }

  if (!doi.startsWith('http')) {
    return 'https://doi.org/' + doi;
  }

  return doi;
}

function copyCitation(doc, format) {
  const title = doc.title || 'Untitled';
  const authors = doc.authors || '';
  const year = doc.document_date ? new Date(doc.document_date).getFullYear() : '';
  const doi = doc.doi ? formatDOI(doc.doi) : '';
  const id = doc.id;

  let citation = '';

  switch (format) {
    case 'RIS':
      citation = `TY  - GEN
TI  - ${title}
AU  - ${authors}
PY  - ${year}
DO  - ${doi}
ER  -`;
      break;
    case 'ENW':
      citation = `%0 Generic
%T ${title}
%A ${authors}
%D ${year}
%U ${doi}`;
      break;
    case 'BIB':
      citation = `@article{reformare${id},
  title={${title}},
  author={${authors}},
  year={${year}},
  doi={${doi}}
}`;
      break;
    default:
      citation = `${title}, ${authors}, ${year}`
  }

  navigator.clipboard.writeText(citation).then(() => {
    alert(`${format} citation copied to clipboard.`);
  });
}

export default Dashboard;

